"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TenantEnvelopeViewSchema = exports.TENANT_ENVELOPE_SIGNER_VIEW_STATUSES = exports.TENANT_ENVELOPE_VIEW_STATUSES = exports.TenantEnvelopeCreateRequestSchema = exports.TenantEnvelopeSignerSchema = exports.TENANT_ENVELOPE_SIGNER_ROLES = exports.AuditCertificateSchema = exports.AuditCertificateSignerSchema = exports.SignatureRecordSchema = exports.EnvelopeSchema = exports.EnvelopePacketItemSchema = exports.EnvelopeInlineDocumentSchema = exports.EnvelopeSourceSchema = exports.EnvelopeBrandSchema = exports.EnvelopeSignerSchema = exports.EnvelopeEventSchema = exports.DEFAULT_ENVELOPE_EXPIRY_DAYS = exports.ENVELOPE_EVENT_TYPES = exports.SIGNATURE_KINDS = exports.SIGNER_IDENTITY_METHODS = exports.SIGNER_STATUSES = exports.SIGNER_ROLES = exports.ENVELOPE_STATUSES = void 0;
exports.canTransitionEnvelope = canTransitionEnvelope;
exports.isEnvelopeTerminal = isEnvelopeTerminal;
exports.signerKeyOf = signerKeyOf;
exports.packetItemRef = packetItemRef;
const zod_1 = require("zod");
const documents_1 = require("./documents");
const documentAcceptance_1 = require("./documentAcceptance");
/**
 * Envelopes, signers, signature records and the audit certificate — the half of
 * document governance that carries legal weight (spec §6).
 *
 * The mechanic is the ORDER of operations (§6.2): render → hash → show the
 * signer THAT EXACT artifact → capture intent → write a signature record that
 * carries the hash they were shown. Every required field below exists to make
 * one of those five steps unforgeable after the fact. Hashing after display
 * would let a bug substitute a different document in between, so the presented
 * hash is required, not optional — a record without it asserts nothing about
 * any particular document and is inadmissible.
 */
/** Trim-aware presence. Blank strings are not evidence (carried from Stage B). */
const hasText = (v) => typeof v === 'string' && v.trim().length > 0;
/** LOWERCASE hex only, one spelling per digest — same rule as VersionArtifact. */
const sha256 = zod_1.z.string().regex(/^[0-9a-f]{64}$/, 'must be 64 lowercase hex characters');
/**
 * Spec §6.1. `executed`, `declined`, `voided` and `expired` are all terminal;
 * see `canTransitionEnvelope`.
 */
exports.ENVELOPE_STATUSES = [
    'draft', 'sent', 'partially_signed', 'executed', 'declined', 'voided', 'expired',
];
/**
 * Who signs. Spec §6.1's example names the countersigner `rubytill`; this
 * package is shared by three products, so the brand is not a value — the
 * countersignature role is `platform`, matching OWNER_SCOPES in ./documents.
 * `witness` covers a manager co-signing an employee document.
 */
exports.SIGNER_ROLES = [
    'platform', 'tenant_admin', 'employee', 'customer', 'witness',
    /**
     * Outside parties added for the attachable-documents engine (spec
     * 2026-08-26): a guarantor personally guaranteeing a financed order, and a
     * business partner co-signing at onboarding. Neither has a login, so their
     * identity method is `email_otp`. Additive — nothing existing changes role.
     */
    'guarantor', 'partner',
    /**
     * The supplier side of a vendor agreement (2026-08-31). Like `guarantor` and
     * `partner` this is an OUTSIDE party with no login, signing through an
     * emailed link and OTP. Appended, never reordered: a role's position is not
     * meaningful, but a stored row naming one that moved would be.
     */
    'vendor',
    /**
     * Ruby Estate leases signed by email (2026-10-15): the resident(s) of a
     * lease, and the landlord business countersigning last. `guarantor` above is
     * reused for lease guarantors. Appended, never reordered.
     */
    'resident', 'landlord',
];
/** A signer's own progress, independent of the envelope's status. */
exports.SIGNER_STATUSES = ['pending', 'opened', 'signed', 'declined'];
/**
 * How the signer was identified (spec §6.3). Step-up is proportionate to how
 * weak the identity is, and is enforced at the surface, not here.
 *
 * `email_otp` is DEFINED BUT UNBUILT: nothing in Stage C implements it. It is
 * declared now so that adding an outside-party signer later is a configuration
 * change rather than a data migration of every existing signature record.
 */
exports.SIGNER_IDENTITY_METHODS = [
    'cognito_session', 'pos_jwt', 'order_ref', 'loyalty_account', 'email_otp',
    /**
     * A STANDING AUTHORISATION, not a live session (2026-08-31).
     *
     * Our own countersignature can be applied without anybody present, from the
     * signature the authorised signatory stored on the company profile. That is a
     * different kind of evidence from a session and must never be recorded as
     * one: `cognito_session` would assert a login that did not happen at signing
     * time. The record carries `appliedAutomatically`, the signatory it acted
     * for, and when their authorisation was captured, so the certificate says
     * what actually occurred.
     */
    'pre_authorised',
];
/**
 * Spec §6.6. A typed name in a script face is LEGALLY EQUIVALENT to a drawn
 * squiggle, and better on desktop. The schema therefore imposes identical
 * requirements on both: neither kind may demand a field the other does not.
 * The image is evidence of intent, not of identity — the session, IP,
 * timestamps and presented hash prove who.
 */
exports.SIGNATURE_KINDS = ['drawn', 'typed'];
/** Every envelope event the audit certificate lists in order (spec §6.5). */
exports.ENVELOPE_EVENT_TYPES = [
    'created', 'sent', 'opened', 'consent_accepted', 'scrolled_to_end',
    'signed', 'declined', 'voided', 'expired', 'executed',
];
/** Spec §6.7: envelopes expire after 30 days by default; expired must be re-issued. */
exports.DEFAULT_ENVELOPE_EXPIRY_DAYS = 30;
/**
 * The envelope state machine.
 *
 * `executed` is TERMINAL. After execution there is no unwind — an executed
 * agreement can only be SUPERSEDED by another agreement (a termination, an
 * amendment). A void or decline path out of `executed` would let someone erase
 * a signed contract, which is exactly the failure this system exists to prevent.
 *
 * `declined`, `voided` and `expired` are terminal for the opposite reason: a
 * dead envelope is re-issued as a NEW envelope, never resurrected, so the
 * evidence trail of the dead one stays intact.
 *
 * `sent -> executed` is allowed: a single-signer envelope never passes through
 * `partially_signed`.
 */
const ALLOWED_ENVELOPE = {
    draft: ['sent', 'voided', 'expired'],
    sent: ['partially_signed', 'executed', 'declined', 'voided', 'expired'],
    partially_signed: ['executed', 'declined', 'voided', 'expired'],
    executed: [],
    declined: [],
    voided: [],
    expired: [],
};
function canTransitionEnvelope(from, to) {
    return ALLOWED_ENVELOPE[from].includes(to);
}
/** True when no transition leads anywhere — the envelope's story is over. */
function isEnvelopeTerminal(status) {
    return ALLOWED_ENVELOPE[status].length === 0;
}
exports.EnvelopeEventSchema = zod_1.z.object({
    type: zod_1.z.enum(exports.ENVELOPE_EVENT_TYPES),
    at: zod_1.z.string().min(1),
    actor: zod_1.z.string().optional(),
    detail: zod_1.z.string().optional(),
});
/**
 * One named signer on one envelope. Stored as its own row (`ENV#<id>` /
 * `SIGNER#<id>`, spec §4.2) and optionally embedded in the envelope view of
 * §6.1 — hence `envelopeId` lives here too.
 *
 * The four timestamps are the ones §6.5 puts on the audit certificate. They are
 * optional here because they accrue one at a time; on the certificate, where the
 * signing is finished, all four are required.
 */
exports.EnvelopeSignerSchema = zod_1.z.object({
    envelopeId: zod_1.z.string().min(1),
    signerId: zod_1.z.string().min(1),
    role: zod_1.z.enum(exports.SIGNER_ROLES),
    /**
     * WHICH SLOT this signer fills (2026-10-15) — `resident_1`, `guarantor_2`,
     * `landlord`. Signatures, signed dates and appendix rows key on it, so two
     * residents can share a role and still own distinct blocks. OPTIONAL and
     * never written onto existing rows: a signer without one IS its role — read
     * it through `signerKeyOf`, never by filling it in at parse time.
     */
    key: zod_1.z.string().trim().min(1).max(40).optional(),
    identityMethod: zod_1.z.enum(exports.SIGNER_IDENTITY_METHODS),
    /** The legal name held to the document. Whitespace is not a name. */
    name: zod_1.z.string().trim().min(1),
    email: zod_1.z.string().email().optional(),
    /** Links the signer back to the acceptance ledger party, when there is one. */
    partyId: zod_1.z.string().optional(),
    /** Countersignature ordering, when an envelope is signed in sequence. */
    signingOrder: zod_1.z.number().int().positive().optional(),
    status: zod_1.z.enum(exports.SIGNER_STATUSES),
    openedAt: zod_1.z.string().optional(),
    consentAcceptedAt: zod_1.z.string().optional(),
    scrolledToEndAt: zod_1.z.string().optional(),
    signedAt: zod_1.z.string().optional(),
    declinedAt: zod_1.z.string().optional(),
    declineReason: zod_1.z.string().optional(),
}).refine((s) => s.status !== 'declined' || hasText(s.declineReason), {
    message: 'a decline must record why — spec §6.7 makes the reason mandatory',
    path: ['declineReason'],
});
/** A signer's key: its explicit `key`, else its role (platform single-signer roles). */
function signerKeyOf(signer) {
    return typeof signer.key === 'string' && signer.key.trim() ? signer.key : signer.role;
}
/**
 * WHOSE NAME THE EMAILS GO OUT UNDER on a tenant envelope (2026-10-15): the
 * landlord business, not the platform. Absent on platform envelopes.
 */
exports.EnvelopeBrandSchema = zod_1.z.object({
    businessName: zod_1.z.string().trim().min(1).max(200),
    replyTo: zod_1.z.string().trim().email().max(254).optional(),
    logoUrl: zod_1.z.string().max(2000).optional(),
});
/**
 * THE APPROVED SOURCE a tenant envelope was raised from — the lease version
 * and its approval stamp (`bodySha256`), restated on the certificate beside
 * the presented hash.
 */
exports.EnvelopeSourceSchema = zod_1.z.object({
    service: zod_1.z.enum(['property']),
    leaseId: zod_1.z.string().min(1).max(80),
    version: zod_1.z.number().int().positive(),
    bodySha256: sha256,
});
/**
 * A tenant envelope's own document: the supplied body, stored as an immutable
 * tenant document version. `sha256` is of the supplied body (markdown or html)
 * as received; `storageKey` locates it when it is held outside the row.
 */
exports.EnvelopeInlineDocumentSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(1).max(300),
    format: zod_1.z.enum(['markdown', 'html']),
    sha256: sha256,
    storageKey: zod_1.z.string().max(1024).optional(),
});
/**
 * An envelope: one template version, one frozen merge set, one set of signers.
 *
 * It names an EXACT template version and never "latest". Publishing v5 of a
 * lease must leave every signed v4 envelope reading exactly as it did on the
 * day it was signed.
 */
/**
 * ONE ITEM IN A PAPERWORK PACKET.
 *
 * A checkout does not present one contract. It presents the device paperwork
 * for every UNIT on the order, the software paperwork, and the deal-wide lines
 * — and the customer signs the device and software half ONCE, having opened
 * each piece. An envelope that could name a single document could not express
 * that, so `agreement` in billing-service refused any order whose deal named
 * two hardware agreements rather than choosing one for the customer.
 *
 * `unitRef` is what makes "per unit" real: two of the same terminal produce two
 * items with the same `documentId@versionNo` and different unit refs, each
 * rendered with that unit's own merge data. It is an ORDINAL, not a serial —
 * no unit is allocated at signing time, so `device_serial` stays a dynamic
 * fill-in rule and is filled at provisioning.
 */
exports.EnvelopePacketItemSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    versionNo: zod_1.z.number().int().positive(),
    /** What the document IS. Kept for display and grouping, never for behaviour. */
    kind: zod_1.z.enum(['agreement', 'policy']),
    /**
     * WHAT THE READER DOES ABOUT THIS ONE — signs it, or answers accept/decline.
     *
     * Frozen onto the item at creation rather than read from the document row at
     * render: changing a document's discharge next year must not change how an
     * envelope already in front of a signer behaves.
     *
     * This is what lets EVERY document travel in one envelope. Before it, a
     * policy could not be in an envelope at all — it was accepted through a
     * separate surface — so a checkout had to split its paperwork across two
     * mechanisms and the customer saw two unrelated lists.
     */
    discharge: zod_1.z.enum(['signature', 'accept_decline']),
    /** What the customer reads in the list — "Dejavoo P17 #2 · Purchase agreement". */
    label: zod_1.z.string().min(1),
    /** Present only on per-unit items. Stable within one envelope. */
    unitRef: zod_1.z.string().optional(),
    /**
     * THE CATALOGUE ROW THIS ITEM IS ABOUT — display only, never a term.
     *
     * Present on per-unit DEVICE items, where one item really is about one SKU.
     * Absent on software and deal-wide items: those are named by the deal's own
     * sections and cover the order rather than a single line, so inventing a SKU
     * for them would be a claim the packet cannot support.
     *
     * ADDITIVE AND OPTIONAL. It exists so a checkout can show the unit's
     * photograph and what it commits to beside the document, instead of the same
     * grey glyph on forty rows. Nothing reads it for behaviour, and an envelope
     * written before it existed renders exactly as it did — the catalogue lookup
     * simply finds nothing and the row falls back to its glyph.
     */
    skuId: zod_1.z.string().optional(),
    /** Which half of the sale it came from, for grouping the list. */
    group: zod_1.z.enum(['device', 'software', 'deal']),
});
/**
 * THE ITEM'S IDENTITY, everywhere: the viewed-tracking set, the render order
 * and the checkout list all key on this. Derived rather than stored so two
 * places can never disagree about what identifies an item.
 *
 * The unit ref is part of it BECAUSE two units of one model share a document
 * and a version — without it, opening unit #1 would mark unit #2 read.
 */
function packetItemRef(item) {
    return `${item.documentId}#${item.versionNo}${item.unitRef ? `@${item.unitRef}` : ''}`;
}
exports.EnvelopeSchema = zod_1.z.object({
    envelopeId: zod_1.z.string().min(1),
    ownerScope: zod_1.z.enum(documents_1.OWNER_SCOPES),
    tenantId: zod_1.z.string().optional(),
    /**
     * WHICH PROCESS this envelope belongs to (spec 2026-08-26 §3.3): the same
     * `{type, refId}` pair a DocumentRequest carries, denormalized onto the
     * envelope so a process's documents are queryable without a client-side
     * join. All three optional and additive — envelopes created before the
     * requests engine simply have none.
     */
    contextType: zod_1.z.string().optional(),
    contextRefId: zod_1.z.string().optional(),
    requestId: zod_1.z.string().optional(),
    /**
     * WHICH STORE this envelope's paperwork belongs to (2026-09-07).
     *
     * Subscriptions, devices and charges are per LOCATION, and the merchant's
     * Documents list was the last surface that could not be divided that way: a
     * two-store account saw one undivided pile of device agreements with no way
     * to tell which shop's terminal each one papered. `contextType`/`contextRefId`
     * could not answer it — onboarding stamps `tenant_onboarding` and the tenant
     * id on every envelope it raises, whatever store the devices are going to.
     *
     * OPTIONAL AND ADDITIVE, and it stays that way: an envelope raised before
     * this field, or one that genuinely covers the whole account (the master
     * services agreement, a deal-wide policy), has none — and "this belongs to no
     * one store" is a real answer that must not be forged into a store id.
     */
    locationId: zod_1.z.string().optional(),
    /**
     * WHO SIGNS FOR US on this one (2026-08-31) — a `signatoryId` from the
     * company profile, when this agreement is countersigned by somebody other
     * than the default. Usually the CEO signs everything and this is absent; a
     * deal a sales rep owns names theirs.
     *
     * Frozen at creation like the merge data, and for the same reason: changing
     * who our default signatory is next year must not change who a contract says
     * signed it. Absent resolves to the profile's default AT SIGNING TIME, which
     * is the correct behaviour for an envelope that never expressed a preference.
     */
    companySignatoryId: zod_1.z.string().optional(),
    /**
     * Frozen template reference. Both halves required; there is no "latest".
     *
     * ON A PACKET THIS IS THE FIRST ITEM, never a summary of the rest. It stays
     * REQUIRED and keeps its exact meaning so every reader, index and executed
     * artifact written before packets existed goes on working untouched — the
     * single-document envelope is simply a packet of one.
     */
    templateDocumentId: zod_1.z.string().min(1),
    templateVersionNo: zod_1.z.number().int().positive(),
    /**
     * EVERY document this one signature binds, in the order they are presented
     * and rendered. Absent on every envelope created before packets, which reads
     * as "just the template above" — additive, with no migration.
     *
     * The signature still binds to ONE hash: the items are rendered in this order
     * into a single artifact, so `presentedSha256` covers the whole packet and
     * the existing evidence model is unchanged.
     */
    templateDocuments: zod_1.z.array(exports.EnvelopePacketItemSchema).optional(),
    /**
     * WHAT DIFFERS BETWEEN ITEMS, keyed by `packetItemRef`.
     *
     * `mergeData` above holds the facts true of the whole order — our identity,
     * the customer, the agreement dates, the software. It cannot also hold
     * `device_model`, because a per-unit packet says something DIFFERENT about
     * each unit, and one flat map can only say one thing.
     *
     * Layered OVER the base map when an item renders, so an item states its own
     * unit and inherits everything else. Frozen at creation like the base map,
     * for the same reason: a signed packet must reproduce byte for byte.
     */
    itemMergeData: zod_1.z.record(zod_1.z.string(), zod_1.z.record(zod_1.z.string())).optional(),
    /**
     * WHICH ITEMS THE SIGNER HAS ACTUALLY OPENED, as `packetItemRef` strings.
     *
     * "The customer must read all of it" is worth nothing as a UI convention — a
     * reload defeats it and no evidence survives. Signing REFUSES while anything
     * is unopened, and this is the record that says so afterwards.
     */
    viewedItems: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * THE ANSWERS to the `accept_decline` items, keyed by `packetItemRef`.
     *
     * A DECLINE IS A REAL ANSWER, not a failure to finish — it is recorded with
     * the same weight as an acceptance, because "they were shown it and said no"
     * is the fact a dispute turns on. What a decline BLOCKS is decided by the
     * caller that owns the process, not here.
     */
    itemDecisions: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        decision: zod_1.z.enum(['accepted', 'declined']),
        at: zod_1.z.string().min(1),
        by: zod_1.z.string().min(1),
    })).optional(),
    /**
     * Resolved ONCE at envelope creation and frozen (spec §4.5). Re-resolving at
     * render time would quietly rewrite history: a tenant who renames their
     * business next year must still read the lease they actually signed. Without
     * this map the artifact cannot be reproduced, and a hash that cannot be
     * reproduced verifies nothing — so the key is REQUIRED.
     *
     * An empty map is legal and meaningful: a template with no merge fields
     * froze nothing. That is not the same as the key being absent, which means
     * nobody ever froze anything. Values are strings, matching the frozen
     * `mergeData` on DocumentVersion — callers stringify before freezing.
     */
    mergeData: zod_1.z.record(zod_1.z.string()),
    status: zod_1.z.enum(exports.ENVELOPE_STATUSES),
    /** The §6.1 composed view. Signers are stored as their own rows (§4.2). */
    signers: zod_1.z.array(exports.EnvelopeSignerSchema).optional(),
    createdBy: zod_1.z.string().min(1),
    createdAt: zod_1.z.string(),
    sentAt: zod_1.z.string().optional(),
    /** Default 30 days from send; an expired envelope is re-issued, never revived. */
    expiresAt: zod_1.z.string().optional(),
    /** Written once, at execution (§6.2). Object-locked bucket, no delete path. */
    executedArtifact: documentAcceptance_1.VersionArtifactSchema.optional(),
    executedAt: zod_1.z.string().optional(),
    declinedAt: zod_1.z.string().optional(),
    declineReason: zod_1.z.string().optional(),
    voidedAt: zod_1.z.string().optional(),
    voidReason: zod_1.z.string().optional(),
    // ── Tenant envelopes (2026-10-15). All optional; platform envelopes never carry them. ──
    brand: exports.EnvelopeBrandSchema.optional(),
    /** Notification category for every email this envelope sends (e.g. `legal`). */
    category: zod_1.z.string().trim().min(1).max(40).optional(),
    source: exports.EnvelopeSourceSchema.optional(),
    document: exports.EnvelopeInlineDocumentSchema.optional(),
})
    .refine((e) => e.ownerScope !== 'tenant' || typeof e.tenantId === 'string', { message: 'tenantId is required when ownerScope is tenant', path: ['tenantId'] })
    .refine((e) => !e.signers || new Set(e.signers.map((s) => s.signerId)).size === e.signers.length, { message: 'signerId must be unique within an envelope', path: ['signers'] })
    /**
     * THE FIRST ITEM IS THE TEMPLATE REFERENCE. Not "usually" — always.
     *
     * Every existing reader (the executed artifact, the envelope list, the audit
     * certificate) resolves the document through `templateDocumentId`. If a
     * packet could put a different document first, those readers would name one
     * document while the signature covered another — a discrepancy nobody would
     * notice until it was evidence in a dispute.
     */
    .refine((e) => !e.templateDocuments?.length
    || (e.templateDocuments[0].documentId === e.templateDocumentId
        && e.templateDocuments[0].versionNo === e.templateVersionNo), {
    message: 'templateDocuments[0] must be the templateDocumentId/templateVersionNo pair — '
        + 'every pre-packet reader resolves the envelope through those two fields',
    path: ['templateDocuments'],
})
    .refine((e) => {
    const refs = (e.templateDocuments ?? []).map(packetItemRef);
    return new Set(refs).size === refs.length;
}, {
    message: 'each packet item must be distinct — two units of one model differ by unitRef, '
        + 'and without it opening one would mark the other read',
    path: ['templateDocuments'],
})
    // Roles are deliberately NOT unique: two co-owners can both sign as tenant_admin.
    // EXPLICIT keys are unique (a key owns one signature block); key-less signers are not checked.
    .refine((e) => {
    const ks = (e.signers ?? []).map((s) => s.key).filter((k) => typeof k === 'string');
    return new Set(ks).size === ks.length;
}, { message: 'signer key must be unique within an envelope', path: ['signers'] })
    .refine((e) => e.status !== 'voided' || hasText(e.voidReason), { message: 'a void must record why — spec §6.7 makes the reason mandatory', path: ['voidReason'] })
    .refine((e) => e.status !== 'declined' || hasText(e.declineReason), { message: 'a decline must record why — spec §6.7 makes the reason mandatory', path: ['declineReason'] })
    .refine((e) => e.status !== 'executed' || !!e.executedArtifact, {
    message: 'an envelope becomes executed only once the executed artifact exists — assemble, hash, write, THEN execute (§6.2)',
    path: ['executedArtifact'],
});
/**
 * The record that carries the legal assertion:
 *   "this identity signed the document whose SHA-256 is X, at this time,
 *    from this place, having explicitly agreed to sign."
 *
 * Any other PDF produced later will not match that hash.
 */
exports.SignatureRecordSchema = zod_1.z.object({
    signatureId: zod_1.z.string().min(1),
    envelopeId: zod_1.z.string().min(1),
    /** The `signerId` of the envelope signer this signature belongs to. */
    signerRef: zod_1.z.string().min(1),
    /** The signer's key (2026-10-15), when it has one. Optional — see `signerKeyOf`. */
    signerKey: zod_1.z.string().trim().min(1).max(40).optional(),
    /** Both kinds carry identical requirements — see SIGNATURE_KINDS. */
    kind: zod_1.z.enum(exports.SIGNATURE_KINDS),
    /**
     * THE field. The SHA-256 of the artifact the signer was actually shown,
     * hashed BEFORE display (§6.2). Never optional, never derived later: a
     * signature record without it is inadmissible.
     */
    presentedSha256: sha256,
    signedAt: zod_1.z.string().min(1),
    identityMethod: zod_1.z.enum(exports.SIGNER_IDENTITY_METHODS),
    /** e.g. the Cognito sub, the POS employee id, the order ref. */
    identityIdentifier: zod_1.z.string().min(1).optional(),
    /** §6.2 step 4: the explicit click on "I agree and sign", beside the mark. */
    agreedToSign: zod_1.z.literal(true),
    /** Evidence of intent, not of identity. Either, both or neither may be set. */
    typedName: zod_1.z.string().optional(),
    signatureImageS3Key: zod_1.z.string().optional(),
    signatureImageSha256: sha256.optional(),
    ip: zod_1.z.string().optional(),
    userAgent: zod_1.z.string().optional(),
    /** Surface context (§6.3): terminal/location/register on POS, order on customer. */
    terminalId: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    registerId: zod_1.z.string().optional(),
    orderId: zod_1.z.string().optional(),
    /** Recorded when a surface required step-up — PIN re-entry on POS is mandatory. */
    stepUpVerifiedAt: zod_1.z.string().optional(),
    /**
     * APPLIED WITHOUT A HUMAN PRESENT — our own countersignature, from the mark
     * the authorised signatory stored on the company profile.
     *
     * Recorded explicitly rather than inferred, because the certificate must not
     * imply a click that never happened. `authorisedBy` names the signatory the
     * signature acted for and `authorisationCapturedAt` is when they gave it, so
     * a reader can see the authorisation predates the signing.
     */
    appliedAutomatically: zod_1.z.literal(true).optional(),
    authorisedBy: zod_1.z.string().min(1).optional(),
    authorisationCapturedAt: zod_1.z.string().min(1).optional(),
}).refine((s) => !s.appliedAutomatically || (hasText(s.authorisedBy) && hasText(s.authorisationCapturedAt)), {
    message: 'an automatically applied signature must name who authorised it and when — without '
        + 'that it asserts a signature nobody can be shown to have agreed to',
    path: ['authorisedBy'],
}).refine((s) => hasText(s.ip) || hasText(s.userAgent), {
    message: 'a signature needs at least one of ip or userAgent — a record with no evidence is not evidence',
    path: ['ip'],
});
/**
 * One signer's block on the audit certificate (spec §6.5). Unlike the live
 * signer row, all four timestamps are REQUIRED here: the certificate is only
 * assembled once signing is finished, and a missing timestamp is a hole in the
 * story the certificate tells.
 */
exports.AuditCertificateSignerSchema = zod_1.z.object({
    signerId: zod_1.z.string().min(1),
    role: zod_1.z.enum(exports.SIGNER_ROLES),
    /** 2026-10-15, optional: the signer key and email the certificate lists. */
    key: zod_1.z.string().trim().min(1).max(40).optional(),
    email: zod_1.z.string().email().optional(),
    name: zod_1.z.string().trim().min(1),
    identityMethod: zod_1.z.enum(exports.SIGNER_IDENTITY_METHODS),
    /** §6.5 names the method AND the identifier; either alone is half an answer. */
    identityIdentifier: zod_1.z.string().min(1),
    ip: zod_1.z.string().optional(),
    userAgent: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    deviceLabel: zod_1.z.string().optional(),
    // The four §6.5 timestamps: opened, e-consent accepted, scrolled to end, signed.
    // Their ORDER is not enforced here — clocks across three surfaces are not a
    // schema's problem, and rejecting a real signature over a millisecond of skew
    // would be worse than recording it as captured.
    openedAt: zod_1.z.string().min(1),
    consentAcceptedAt: zod_1.z.string().min(1),
    scrolledToEndAt: zod_1.z.string().min(1),
    signedAt: zod_1.z.string().min(1),
});
/**
 * Appended as the final pages of every executed PDF, and the payload the public
 * verify endpoint answers from. It carries hashes, signer summary and status —
 * NEVER document content. Anyone holding the PDF can confirm it is genuine
 * without us sending them anything.
 */
exports.AuditCertificateSchema = zod_1.z.object({
    envelopeId: zod_1.z.string().min(1),
    documentTitle: zod_1.z.string().min(1),
    /** The exact template version, restated on the certificate itself. */
    templateDocumentId: zod_1.z.string().min(1),
    templateVersionNo: zod_1.z.number().int().positive(),
    effectiveAt: zod_1.z.string().optional(),
    /** What the signers saw, and what execution froze. Both, always. */
    presentedSha256: sha256,
    executedSha256: sha256,
    /** Stamped so the signed document can be reproduced byte-for-byte later. */
    rendererVersion: zod_1.z.string().min(1),
    /** 2026-10-15, optional: the approved source's own hash (lease version `bodySha256`). */
    sourceBodySha256: sha256.optional(),
    signers: zod_1.z.array(exports.AuditCertificateSignerSchema).min(1),
    /** Every envelope event, in order. A certificate with no history is a claim. */
    events: zod_1.z.array(exports.EnvelopeEventSchema).min(1),
    /** Printed on the certificate so a third party can check it themselves. */
    verificationUrl: zod_1.z.string().url(),
    generatedAt: zod_1.z.string().min(1),
});
// ── Tenant envelope internal API (2026-10-15, contracts C1 + C2) ──────────────
/** The roles a tenant (lease) envelope may name. `landlord` always signs last. */
exports.TENANT_ENVELOPE_SIGNER_ROLES = ['resident', 'guarantor', 'landlord'];
/**
 * C1 — one signer on a tenant envelope. `key` is unique within the envelope and
 * starts with its role (`resident_1`, `guarantor_2`, `landlord`). Residents and
 * guarantors prove identity by `email_otp`; the landlord is `pre_authorised`
 * (signs in-app after merchant auth).
 */
exports.TenantEnvelopeSignerSchema = zod_1.z.object({
    key: zod_1.z.string().regex(/^(resident_\d{1,2}|guarantor_\d{1,2}|landlord)$/, 'key must be resident_N, guarantor_N or landlord'),
    role: zod_1.z.enum(exports.TENANT_ENVELOPE_SIGNER_ROLES),
    name: zod_1.z.string().trim().min(1).max(160),
    email: zod_1.z.string().trim().toLowerCase().email().max(254),
    identity: zod_1.z.enum(['email_otp', 'pre_authorised']),
}).superRefine((s, ctx) => {
    const keyRole = s.key === 'landlord' ? 'landlord' : s.key.split('_')[0];
    if (keyRole !== s.role)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['role'], message: `key ${s.key} does not belong to role ${s.role}` });
    const want = s.role === 'landlord' ? 'pre_authorised' : 'email_otp';
    if (s.identity !== want)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['identity'], message: `${s.role} must use ${want}` });
});
/**
 * C2 — `POST /internal/tenant-envelopes` body. Keys unique, at least one
 * non-landlord signer, and if a landlord is present it is the LAST signer.
 */
exports.TenantEnvelopeCreateRequestSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1).max(80),
    title: zod_1.z.string().trim().min(1).max(300),
    markdown: zod_1.z.string().min(1).max(120000),
    mergeData: zod_1.z.record(zod_1.z.string()),
    signers: zod_1.z.array(exports.TenantEnvelopeSignerSchema).min(2).max(16),
    brand: zod_1.z.object({ businessName: zod_1.z.string().trim().min(1).max(200), replyTo: zod_1.z.string().trim().email().max(254).optional() }),
    source: exports.EnvelopeSourceSchema,
    expiresInDays: zod_1.z.number().int().min(1).max(365).default(exports.DEFAULT_ENVELOPE_EXPIRY_DAYS),
    /** Final wave I5: a repeat create with the same tenantId + key returns the existing envelope. */
    idempotencyKey: zod_1.z.string().trim().min(1).max(200).optional(),
}).superRefine((b, ctx) => {
    const keys = b.signers.map((s) => s.key);
    if (new Set(keys).size !== keys.length)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['signers'], message: 'signer keys must be unique' });
    const li = b.signers.findIndex((s) => s.role === 'landlord');
    if (li !== -1 && li !== b.signers.length - 1)
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['signers'], message: 'landlord must be the last signer' });
    if (!b.signers.some((s) => s.role !== 'landlord'))
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['signers'], message: 'at least one non-landlord signer is required' });
});
/** C2 view statuses: an envelope as the caller sees it (never `draft`). */
exports.TENANT_ENVELOPE_VIEW_STATUSES = ['sent', 'partially_signed', 'executed', 'declined', 'voided', 'expired'];
/** C2 per-signer view statuses (the engine's pending/opened read as sent/viewed). */
exports.TENANT_ENVELOPE_SIGNER_VIEW_STATUSES = ['sent', 'viewed', 'signed', 'declined'];
/** C2 — `GET /internal/tenant-envelopes/{id}?tenantId=` response. */
exports.TenantEnvelopeViewSchema = zod_1.z.object({
    envelopeId: zod_1.z.string().min(1),
    status: zod_1.z.enum(exports.TENANT_ENVELOPE_VIEW_STATUSES),
    signers: zod_1.z.array(zod_1.z.object({
        key: zod_1.z.string().min(1).max(40),
        role: zod_1.z.enum(exports.TENANT_ENVELOPE_SIGNER_ROLES),
        name: zod_1.z.string(),
        email: zod_1.z.string(),
        status: zod_1.z.enum(exports.TENANT_ENVELOPE_SIGNER_VIEW_STATUSES),
        signedAt: zod_1.z.string().optional(),
        declinedReason: zod_1.z.string().optional(),
    })),
    executedPdfUrl: zod_1.z.string().optional(),
    certificateUrl: zod_1.z.string().optional(),
});
//# sourceMappingURL=documentSigning.js.map